import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { AlertTriangle, Edit, Package, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader, LoadingState } from '@/components/common';
import { DetailLayout, DetailList, MetricBars, RailCard } from '@/components/common/DetailPanels';
import { riskManagementApi, type RiskProfile } from '@/api/services/risk-management.api';
import { formatDateTime, formatCurrency } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';
import { getRiskBadgeVariant, getRiskLabel, getRiskBarClass } from '@/lib/risk';

/**
 * One item's risk assessment.
 *
 * This lived in a sheet on the Risk Management list, which meant the assessment
 * that decided an item was critical could not be linked to or returned to — and
 * "here is why we flagged this spare" is exactly the kind of thing somebody
 * sends a colleague.
 */
export default function RiskProfileDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<RiskProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    riskManagementApi
      .getById(id)
      .then((res) => !cancelled && setProfile(res.data))
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/risk');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!profile) return null;

  /**
   * The five component risks share one scale, so they can be compared as bars.
   * The overall score is deliberately not among them — it is a roll-up of these
   * five, and drawing it alongside would invite reading it as a sixth.
   */
  const componentRisks = [
    { label: 'Supply', value: profile.supplyRisk },
    { label: 'Demand', value: profile.demandRisk },
    { label: 'Quality', value: profile.qualityRisk },
    { label: 'Price', value: profile.priceRisk },
    { label: 'Obsolescence', value: profile.obsolescenceRisk },
  ].map((r) => ({ ...r, className: getRiskBarClass(r.value) }));

  const flags = [
    { label: 'Single sourced', on: profile.singleSourced },
    { label: 'Critical item', on: profile.criticalItem },
    { label: 'Long lead time', on: profile.longLeadTime },
    { label: 'High value', on: profile.highValue },
    { label: 'Volatile pricing', on: profile.volatilePricing },
  ].filter((f) => f.on);

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Risk Management', href: '/risk' },
            { label: profile.item?.sku ?? 'Profile' },
          ]}
          title={profile.item?.name ?? 'Risk profile'}
          description={profile.item?.sku}
          titleAdornment={
            <Badge variant={getRiskBadgeVariant(profile.overallRisk)} className="px-3 py-1">
              {profile.overallRisk}/10 · {getRiskLabel(profile.overallRisk)}
            </Badge>
          }
          actions={
            <Button variant="outline" onClick={() => navigate('/risk')}>
              <Edit className="mr-2 h-4 w-4" />
              Edit on the list
            </Button>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard title="Item" icon={<Package className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    {
                      label: 'SKU',
                      value: profile.item ? (
                        <Link
                          to={`/inventory/${profile.itemId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {profile.item.sku}
                        </Link>
                      ) : (
                        '—'
                      ),
                    },
                    {
                      label: 'Category',
                      value: profile.item?.category,
                      when: !!profile.item?.category,
                    },
                    {
                      label: 'Unit cost',
                      value: formatCurrency(profile.item?.unitCost ?? 0),
                      when: profile.item?.unitCost != null,
                    },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Assessment"
                icon={<ShieldAlert className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    {
                      label: 'Overall risk',
                      value: `${profile.overallRisk}/10 · ${getRiskLabel(profile.overallRisk)}`,
                      tone:
                        profile.overallRisk >= 8
                          ? 'destructive'
                          : profile.overallRisk >= 6
                            ? 'warning'
                            : 'default',
                    },
                    { label: 'Safety stock', value: `${profile.safetyStockDays} days` },
                    {
                      label: 'Alternate suppliers',
                      value: profile.alternateSuppliers?.length ?? 0,
                    },
                    {
                      label: 'Last assessed',
                      value: profile.lastAssessedAt
                        ? formatDateTime(profile.lastAssessedAt)
                        : 'Never',
                      // An assessment nobody has revisited is the one worth
                      // noticing on a page about risk.
                      tone: profile.lastAssessedAt ? 'default' : 'warning',
                    },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                    <AlertTriangle className="h-5 w-5" />
                    Risk breakdown
                  </h3>
                  <CardDescription>
                    The five components behind the overall score of {profile.overallRisk}/10
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <MetricBars rows={componentRisks} unit="/10" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Risk factors</h3>
                  <CardDescription>Conditions recorded against this item</CardDescription>
                </CardHeader>
                <CardContent>
                  {flags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {flags.map((f) => (
                        <Badge key={f.label} variant="outline">
                          {f.label}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No specific risk factors identified.
                    </p>
                  )}
                </CardContent>
              </Card>

              {(profile.mitigationStrategy || profile.notes) && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">
                      Mitigation and notes
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {profile.mitigationStrategy && (
                      <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Mitigation strategy
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {profile.mitigationStrategy}
                        </p>
                      </div>
                    )}
                    {profile.notes && (
                      <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Notes
                        </p>
                        <p className="text-sm text-muted-foreground">{profile.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
